import React, { useMemo, useRef, useState } from 'react';
import { LuCirclePlus, LuSearch } from 'react-icons/lu';

import { AllocationType, Strategy, StrategyType } from '@ndvr/portfolio-api';

import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';

import {
    getStrategyHoldingsLabel,
    getStrategyTypeBadge,
    getStrategyTypeLabel,
    isSelectableStrategy,
} from '../../strategies/strategy-utils';

const MAX_RESULTS = 8;

interface AddStrategyRowProps {
    query: string;
    onQueryChange: (query: string) => void;
    availableStrategies: Strategy[];
    usedStrategyGUIDs: Set<string>;
    allocationType: AllocationType;
    onAddExisting: (strategyGUID: string) => void;
    onOpenBuilder: () => void;
}

export const AddStrategyRow: React.FC<AddStrategyRowProps> = ({
    query,
    onQueryChange,
    availableStrategies,
    usedStrategyGUIDs,
    allocationType,
    onAddExisting,
    onOpenBuilder,
}) => {
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const trimmedQuery = query.trim().toLowerCase();

    const matches = useMemo(() => {
        if (!trimmedQuery) return [];
        return availableStrategies
            .filter((s) => isSelectableStrategy(s) && !usedStrategyGUIDs.has(s.strategyGUID))
            .filter((s) => allocationType !== AllocationType.SHARED || s.strategyType !== StrategyType.PERSONAL)
            .filter((s) => s.strategyName.toLowerCase().includes(trimmedQuery))
            .slice(0, MAX_RESULTS);
    }, [availableStrategies, usedStrategyGUIDs, allocationType, trimmedQuery]);

    return (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Popover open={isSearchOpen && !!trimmedQuery} onOpenChange={setIsSearchOpen}>
                <PopoverAnchor asChild>
                    <div className="border-cocoa-200 flex flex-col gap-2.5 rounded-lg border bg-white p-4">
                        <div className="flex items-center gap-2">
                            <LuSearch className="text-seafoam-600 h-4.5 w-4.5" />
                            <span className="text-cocoa-1000 text-sm font-medium">
                                Search the Model Library to add a strategy
                            </span>
                        </div>
                        <input
                            ref={inputRef}
                            aria-label="Search strategies"
                            value={query}
                            onFocus={() => setIsSearchOpen(true)}
                            onChange={(e) => {
                                onQueryChange(e.target.value);
                                setIsSearchOpen(true);
                            }}
                            placeholder="strategy name…"
                            className="border-cocoa-200 h-9 rounded-md border px-2.5 text-sm outline-none"
                        />
                    </div>
                </PopoverAnchor>
                <PopoverContent
                    align="start"
                    sideOffset={6}
                    collisionPadding={12}
                    onOpenAutoFocus={(event) => event.preventDefault()}
                    onCloseAutoFocus={(event) => event.preventDefault()}
                    onInteractOutside={(event) => {
                        if (event.target === inputRef.current) event.preventDefault();
                    }}
                    className="border-cocoa-200 w-(--radix-popover-trigger-width) overflow-hidden rounded-lg bg-white p-0 tracking-[-0.02em]"
                >
                    <div className="max-h-[min(20rem,var(--radix-popover-content-available-height))] overflow-y-auto">
                        {matches.length === 0 ? (
                            <div className="text-cocoa-500 px-3 py-4 text-center text-sm">No matching strategies.</div>
                        ) : (
                            matches.map((strategy, index) => (
                                <button
                                    key={strategy.strategyGUID}
                                    type="button"
                                    onClick={() => {
                                        onAddExisting(strategy.strategyGUID);
                                        onQueryChange('');
                                        setIsSearchOpen(false);
                                        inputRef.current?.focus();
                                    }}
                                    className={`hover:bg-cocoa-0 flex w-full items-center gap-2 px-3 py-2.5 text-left ${
                                        index > 0 ? 'border-cocoa-200/50 border-t' : ''
                                    }`}
                                >
                                    <span className="bg-cocoa-0 text-cocoa-500 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold tracking-wide uppercase">
                                        {getStrategyTypeBadge(strategy)?.label ?? getStrategyTypeLabel(strategy)}
                                    </span>
                                    <span className="text-cocoa-1000 truncate text-sm font-medium">
                                        {strategy.strategyName}
                                    </span>
                                    <span className="text-cocoa-500 ml-auto shrink-0 text-xs">
                                        {getStrategyHoldingsLabel(strategy)}
                                    </span>
                                </button>
                            ))
                        )}
                    </div>
                </PopoverContent>
            </Popover>

            <button
                type="button"
                onClick={onOpenBuilder}
                className="border-cocoa-200 hover:bg-cocoa-0 hover:border-cocoa-300 flex flex-col gap-2.5 rounded-lg border bg-white p-4 text-left transition-colors"
            >
                <div className="flex items-center gap-2">
                    <LuCirclePlus className="h-4.5 w-4.5" />
                    <span className="text-cocoa-1000 text-sm font-medium">Create new strategy</span>
                </div>
                <span className="text-cocoa-500 text-xs">Build it here and it drops straight into this Model.</span>
            </button>
        </div>
    );
};
